import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { Form, Button, Row, Col, Modal, Container } from "react-bootstrap";
import axios from "axios";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react"; // React Data Grid Component
import { useSelector } from "react-redux";
import {
  agGridOptionsSingle,
  agGridTheme,
  HOST_URL,
} from "../../helpers/constants/Constants";
import Comments from "../comments/Comments";

const Reviews = () => {
  const { allProjectReviews, selectedProject } = useSelector(
    (state) => state.globalReducer,
  );
  const userData = useSelector((state) => state.userData.data);
  const [rowData, setRowData] = useState([]);
  const [gridApi, setGridApi] = useState();
  const [infoModal, setInfoModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [gridPageSizeMode, setGridPageSizeMode] = useState("auto");

  useEffect(() => {
    if (allProjectReviews) {
      setRowData(allProjectReviews);
    }
  }, [allProjectReviews]);

  const onCloseInfoModal = () => {
    setInfoModal(false);
  };

  const reviewStatus = (data) => {
    if (
      data?.approvedDate?.toLowerCase() === "rejected" ||
      data?.reviewedDate?.toLowerCase() === "rejected" ||
      data?.checkedDate?.toLowerCase() === "rejected" ||
      data?.submittedDate?.toLowerCase() === "rejected"
    ) {
      return "Rejected";
    } else if (
      data?.approvedDate?.toLowerCase() === "archived" ||
      data?.reviewedDate?.toLowerCase() === "archived" ||
      data?.checkedDate?.toLowerCase() === "archived" ||
      data?.submittedDate?.toLowerCase() === "archived"
    ) {
      return "Archived";
    } else if (data?.approvedDate) {
      return "Approved";
    } else if (data?.reviewedDate) {
      return "Reviewed";
    } else if (data?.checkedDate) {
      return "Checked";
    } else if (data?.submittedDate) {
      return "Submitted";
    } else {
      return "Not Started";
    }
  };

  const fitGridColumns = useCallback(
    (apiInput) => {
      const api = apiInput || gridApi;
      if (!api) return;

      api.sizeColumnsToFit();
    },
    [gridApi],
  );

  const applyGridPageSizeMode = useCallback(
    (apiInput) => {
      const api = apiInput || gridApi;
      if (!api) return;

      const allRowCount = Math.max(rowData?.length || 0, 1);

      if (gridPageSizeMode === "all") {
        api.setGridOption("paginationAutoPageSize", false);
        api.setGridOption("paginationPageSize", allRowCount);
      } else if (gridPageSizeMode === "auto") {
        api.setGridOption("paginationAutoPageSize", true);
      } else {
        const pageSize = Number(gridPageSizeMode) || 25;
        api.setGridOption("paginationAutoPageSize", false);
        api.setGridOption("paginationPageSize", pageSize);
      }

      api.paginationGoToFirstPage();
    },
    [gridApi, gridPageSizeMode, rowData],
  );

  useEffect(() => {
    applyGridPageSizeMode();
    fitGridColumns();
  }, [applyGridPageSizeMode, fitGridColumns]);

  const gridReady = (params) => {
    setGridApi(params.api);
    requestAnimationFrame(() => {
      fitGridColumns(params.api);
      applyGridPageSizeMode(params.api);
    });
  };

  const [colDefs] = useState([
    { field: "id", hide: true },
    {
      colId: "Document Number",
      valueGetter: (p) => p.data?.projectAssetTask?.docNumber,
      rowGroup: true,
      hide: true,
      sort: "asc",
    },
    {
      headerName: "Regional Designation",
      valueGetter: (p) => p.data?.projectAssetTask?.projectAsset.asset.name,
      // checkboxSelection: true,
    },
    {
      headerName: "MWH Asset",
      valueGetter: (p) => p.data?.projectAssetTask?.mwhAssetName,
    },
    {
      headerName: "Title",
      valueGetter: (p) => p.data?.projectAssetTask?.task,
    },
    {
      headerName: "Discipline",
      valueGetter: (p) => p.data?.projectAssetTask?.discipline,
    },
    {
      headerName: "Requested Status",
      valueGetter: (p) => p.data?.proposedStatus,
    },
    {
      headerName: "Review Status",
      valueGetter: (p) => reviewStatus(p.data),
    },
    {
      headerName: "Originator",
      valueGetter: (p) => p.data?.submittedBy,
    },
    {
      headerName: "Initiated On",
      valueGetter: (p) => p.data?.submittedDate,
    },
    {
      headerName: "Checker",
      valueGetter: (p) => p.data?.checkedBy,
    },
    {
      headerName: "Checked On",
      valueGetter: (p) => p.data?.checkedDate,
    },
    {
      headerName: "Reviewer",
      valueGetter: (p) => p.data?.reviewedBy,
    },
    {
      headerName: "Reviewed On",
      valueGetter: (p) => p.data?.reviewedDate,
    },
    {
      headerName: "Approver",
      valueGetter: (p) => p.data?.approvedBy,
    },
    {
      headerName: "Approved On",
      valueGetter: (p) => p.data?.approvedDate,
    },
  ]);

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
    }),
    [],
  );

  const gridRef = useRef();

  const selectionChanged = () => {
    setSelectedFiles(gridApi.getSelectedRows());
  };

  const autoGroupColumnDef = useMemo(() => {
    return {
      headerName: "Name",
      valueGetter: (p) => p.data?.projectAssetTask.projectAsset.asset.name,
      minWidth: 250,
      cellRenderer: "agGroupCellRenderer",
    };
  }, []);

  const updateReviewComments = async (review, comments) => {
    //updateSelectedReview with new comments
    setSelectedFiles([{ ...review, comments }]);
    await axios({
      method: "post",
      url: HOST_URL + "/phReviews/updateReviewComments",
      withCredentials: true,
      data: { reviewId: review.id, comments },
    });
  };

  return (
    <div className="project-config-page">
      <Container className="project-config-shell">
        <h1 className="project-config-title">Reviews</h1>
        <Row>
          <Col
            className="project-config-actions"
            style={{
              display: "flex",
              justifyContent: "space-around",
              alignItems: "center",
              paddingTop: "8px",
              flexWrap: "wrap",
              gap: "0.5rem",
            }}
          >
            <Button
              className="project-config-action-btn mwh-btn-primary"
              variant="outline-info"
              size="sm"
              style={{ width: "150px" }}
              onClick={() => {
                setInfoModal(true);
              }}
              disabled={selectedFiles?.length === 0}
            >
              <span>View More Info</span>
            </Button>
          </Col>
        </Row>
        <Row className="project-config-grid-row">
          <div className="project-config-grid-wrap">
            <div className="project-config-grid-table">
              <AgGridReact
                theme={agGridTheme}
                rowData={rowData}
                columnDefs={colDefs}
                defaultColDef={defaultColDef}
                ref={gridRef}
                onSelectionChanged={selectionChanged}
                groupDisplayType={"groupRows"}
                autoGroupColumnDef={autoGroupColumnDef}
                onGridReady={gridReady}
                onGridSizeChanged={(params) => {
                  fitGridColumns(params.api);
                  if (gridPageSizeMode === "auto") {
                    applyGridPageSizeMode(params.api);
                  }
                }}
                onFirstDataRendered={(params) => {
                  fitGridColumns(params.api);
                  applyGridPageSizeMode(params.api);
                }}
                paginationAutoPageSize={gridPageSizeMode === "auto"}
                paginationPageSizeSelector={false}
                gridOptions={agGridOptionsSingle}
              />
              <div className="project-config-grid-rows-view">
                <Form.Label className="mb-0 me-2">Page size</Form.Label>
                <Form.Select
                  size="sm"
                  value={gridPageSizeMode}
                  onChange={(e) => {
                    setGridPageSizeMode(e.target.value);
                  }}
                  style={{ maxWidth: "180px" }}
                >
                  <option value="auto">Auto</option>
                  <option value="25">25 per page</option>
                  <option value="50">50 per page</option>
                  <option value="100">100 per page</option>
                  <option value="all">All (scroll)</option>
                </Form.Select>
              </div>
            </div>
          </div>
        </Row>
      </Container>
      {/* MODALS */}
      <Modal
        show={infoModal}
        onHide={onCloseInfoModal}
        dialogClassName="markup-modal project-config-modal"
        centered
      >
        <Modal.Header className="project-config-modal__header">
          <Modal.Title>Details:</Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close"
              onClick={onCloseInfoModal}
              aria-label="Close"
            />
          </div>
        </Modal.Header>
        <Modal.Body className="project-config-modal__body">
          <div className="row">
            <div className="col ">
              <Form.Label className="mt-1">Project</Form.Label>
              <Form.Control
                className="form-control form-control-sm"
                readOnly
                value={selectedProject[0]?.text}
              />
              <Form.Label className="mt-1">Regional Designation</Form.Label>
              <Form.Control
                className="form-control form-control-sm"
                readOnly
                value={
                  selectedFiles[0]?.projectAssetTask?.projectAsset?.asset?.name
                }
              />
              <Form.Label className="mt-1">MWH Asset</Form.Label>
              <Form.Control
                className="form-control form-control-sm"
                readOnly
                value={selectedFiles[0]?.projectAssetTask?.mwhAssetName}
              />
              <Form.Label className="mt-1">Model</Form.Label>
              <Form.Control
                className="form-control form-control-sm"
                readOnly
                value={selectedFiles[0]?.projectAssetTask?.docNumber}
              />
              <Form.Label className="mt-1">Target status</Form.Label>
              <Form.Control
                className="form-control form-control-sm"
                readOnly
                value={selectedFiles[0]?.proposedStatus}
              />
              <Form.Label className="mt-1">Submitted By</Form.Label>
              <Form.Control
                className="form-control form-control-sm"
                readOnly
                value={selectedFiles[0]?.submittedBy}
              />
            </div>
            <div className="col ">
              <Form.Label className="form-label mt-1">Comments</Form.Label>
              <div style={{ height: "200px" }}>
                <Comments
                  currentUser={userData?.emailId}
                  comments={selectedFiles[0]?.comments}
                  updateReview={(e) => {
                    updateReviewComments(selectedFiles[0], e);
                  }}
                />
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Reviews;
